/* Branded PDF export for the project report.

   Laid out to match the formal review-memo template: logo letterhead, green #388E3C header
   rows, the memo's priority shading (#FDECEA / #FFF8E1 / #ECEFF1), a running head and a page
   footer. The previous export was a slate-navy banner in plain Helvetica with no logo — it
   looked like neither the app nor the documents we send clients.

   Split out of ReportPage so it can be rendered and eyeballed outside a browser
   (scripts/preview-report-pdf.js). */

import jsPDF from 'jspdf';
import autoTable, { CellHook, CellInput, HookData, RowInput, UserOptions } from 'jspdf-autotable';
import {
  Project, DaltResult, EbtronResult, PowerResult, FacilityAssessment,
  BoilerECMResult, PMSchedule, WorkOrder, ComplianceRecord,
  FacilityAsset, FacilityPhoto, CriterionKey, RatingLabel,
} from '../models/types';
import {
  getBuildingRating, maxPossibleScore, getCriterionPoints, isAssetAssessed, assessedAssetCount,
  totalAssetCount, getDowngradeNote, getAssetConditionText, generateRecommendations,
} from '../modules/facility/facilityCalculations';

export const SYSTEM_LABELS = {
  controls: 'Controls', electrical: 'Electrical', mechanical: 'Mechanical', plumbing: 'Plumbing',
};
export const SYSTEM_KEYS = ['controls', 'electrical', 'mechanical', 'plumbing'] as const;

export const LOAD_LABELS: Record<string, string> = {
  'three-phase-inductive': '3-Phase Inductive',
  'single-phase-inductive': '1-Phase Inductive',
  'single-phase-resistive': '1-Phase Resistive',
};

export const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/sp_logo_v2.png`;
/* sp_logo_v2.png is 1600x323. The letterhead spec is ~2.6in wide. */
const LOGO_W_MM = 66;
const LOGO_H_MM = (LOGO_W_MM * 323) / 1600;

/* Mark only (no wordmark), square, transparent-on-white: used where the wordmark needs to be
   real jsPDF text rather than baked into the raster — the detailed report's cover brand lockup. */
export const MARK_SRC = `${process.env.PUBLIC_URL || ''}/sp_mark_v2.png`;
const MARK_MM = 15;

/* One-colour white mark, alpha-matted (qlmanage flattens SVG renders to opaque white, so this
   was extracted via difference matting — rendered once on black, once on white, and the true
   alpha recovered from the difference). For dark grounds — title page option 1b's left panel —
   where the full-colour mark's pale tints would fail contrast, same reasoning as the AppShell
   dark sider's mono mark. */
export const MARK_WHITE_SRC = `${process.env.PUBLIC_URL || ''}/sp_mark_v2_white.png`;

/** "14 Aug 2026" — the cover's date format. Falls back to the raw string if unparseable. */
const formatCoverDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/* No report-numbering scheme exists yet, so the ID is derived deterministically from the project
   name and today's date rather than stored — same report, same day, same ID. Revisit if/when
   real report numbering (and reissue/revision tracking) becomes a product need.

   The tag is the project name's word initials (up to 6), not its first four letters: two projects
   for the same client and building — "GoDurham Fixed Route Maintenance Building" and "... Controls"
   — both truncated to GODU and would have gone out the same day with the identical report ID. */
export const deriveReportId = (projectName: string, date: Date): string => {
  const digits = projectName.match(/\d+/)?.[0];
  const words = projectName.split(/[^A-Za-z0-9]+/).filter(Boolean);
  const initials = words.length >= 2 ? words.map(w => w[0]).join('').slice(0, 6).toUpperCase() : '';
  const tag = digits || initials || projectName.replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase() || 'RPT';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `FCA-${tag}-${mm}${dd}`;
};

/* The brand palette in the RGB triples jsPDF wants, named after the memo template's roles so
   the PDF, the screen report and the Word deliverables agree. */
type RGB = [number, number, number];
const PDF: Record<string, RGB> = {
  green: [56, 142, 60],       // #388E3C - header rows, rules
  slate: [38, 50, 56],        // #263238 - body text
  muted: [140, 140, 140],     // #8C8C8C - captions, footer
  hairline: [224, 228, 230],  // #E0E4E6 - cell borders
  zebra: [250, 250, 250],     // #FAFAFA
  rowDanger: [253, 236, 234], // #FDECEA - overdue / fail
  rowWarn: [255, 248, 225],   // #FFF8E1 - due soon
  rowMuted: [236, 239, 241],  // #ECEFF1 - informational
  rowTotal: [241, 248, 233],  // #F1F8E9 - totals
  white: [255, 255, 255],
  lightGreen: [165, 214, 167], // #A5D6A7 - on-slate accent (matches the logo tagline)
  coverInk: [90, 97, 102],     // #5A6166 - cover-only body/label grey; clears 4.5:1 on white
                                // (PDF.muted/#8C8C8C does not, but that's used report-wide, so
                                // it's left alone rather than changed here)
  coverBorder: [222, 228, 226], // #DEE4E2 - cover card/rule borders
  panelGreen: [30, 70, 32],     // #1E4620 - dark left panel, title page option 1b
};

/* The cover's overall-condition scale. Five visual bands over the app's existing seven-tier
   RatingLabel (facilityCalculations.ts), not a second scoring system — RATING_TO_BAND is the only
   new mapping; the underlying scores/ratings are unchanged. */
const BAND_ORDER = ['Poor', 'Marginal', 'Fair', 'Good', 'Excellent'] as const;
type ConditionBand = typeof BAND_ORDER[number];
const RATING_TO_BAND: Record<RatingLabel, ConditionBand> = {
  Failed: 'Poor', Critical: 'Poor', Poor: 'Marginal', Average: 'Fair', Good: 'Good',
  'Like New': 'Excellent', New: 'Excellent',
};
const BAND_COLORS: Record<ConditionBand, { bar: RGB; bg: RGB; ink: RGB }> = {
  Poor: { bar: [198, 40, 40], bg: [251, 234, 234], ink: [163, 34, 34] },
  Marginal: { bar: [239, 140, 46], bg: [253, 240, 228], ink: [154, 87, 22] },
  Fair: { bar: [229, 185, 60], bg: [251, 244, 224], ink: [138, 106, 18] },
  Good: { bar: [127, 176, 74], bg: [241, 247, 232], ink: [74, 107, 34] },
  Excellent: { bar: [46, 139, 63], bg: [233, 244, 236], ink: [31, 107, 44] },
};

/* jsPDF's built-in Helvetica is WinAnsi (cp1252) encoded. A character outside that set doesn't
   just render wrong — "≤3 years RSL" came out as `"d3 years RSL` — it also drops the whole run
   into a per-glyph drawing path that renders visibly letter-spaced. So map the symbols this app
   actually emits to ASCII, then strip anything else that cp1252 can't hold. Em/en dashes, ×, ²,
   • and smart quotes are all in cp1252 and pass through untouched. */
const ASCII_FALLBACK: Record<string, string> = {
  '≤': '<=', '≥': '>=', '≈': '~', '≠': '!=',
  '→': '->', '✓': 'Yes', '✗': 'No',
};
/** The cp1252-only characters (0x80–0x9F), which Latin-1 doesn't cover. */
const CP1252_EXTRAS = '€‚ƒ„…†‡ˆ‰Š‹Œ'
  + 'Ž‘’“”•–—˜™š›œžŸ';
// Tab and newline are kept: jsPDF's text()/splitTextToSize and autoTable all break lines on \n, so
// preserving them is what makes a multi-line note render as multiple lines instead of one run.
const UNENCODABLE = new RegExp(`[^\\t\\n\\u0020-\\u00ff${CP1252_EXTRAS}]`, 'g');

/** Makes a string safe for jsPDF's standard fonts. */
export const winAnsi = (text: string) =>
  text
    .replace(/\r\n?/g, '\n')   // normalize CRLF / CR to the \n jsPDF splits on
    .replace(/[≤≥≈≠→✓✗]/g, c => ASCII_FALLBACK[c])
    .replace(UNENCODABLE, '');

export interface ReportData {
  project: Project;
  daltResults: DaltResult[];
  ebtronResults: EbtronResult[];
  powerResults: PowerResult[];
  facilityAssessments: FacilityAssessment[];
  boilerECMResults: BoilerECMResult[];
  pmSchedules: PMSchedule[];
  workOrders: WorkOrder[];
  complianceRecords: ComplianceRecord[];
}

/** Executive-summary figures, shared by the on-screen report and the PDF export. */
export const summarize = (d: ReportData) => {
  const today = new Date().toISOString().slice(0, 10);
  const activePM = d.pmSchedules.filter(s => s.status === 'active');
  const overduePM = activePM.filter(s => s.nextDueDate < today);
  const openWOs = d.workOrders.filter(w => w.status === 'open' || w.status === 'in-progress');
  const urgentWOs = openWOs.filter(w => w.priority === 'P1' || w.priority === 'P2');
  const overdueCompliance = d.complianceRecords.filter(r => r.status === 'overdue');
  const total = d.complianceRecords.length;
  const complianceHealthScore = total === 0
    ? 100
    : Math.round((d.complianceRecords.filter(r => r.status === 'current').length / total) * 100);
  return { today, activePM, overduePM, openWOs, urgentWOs, overdueCompliance, complianceHealthScore };
};

/* Resolves to null rather than rejecting: one unreachable image must not cost you the PDF.
   `crossOrigin` matters for the Storage-hosted asset photos — without it the decoded bitmap is
   tainted and jsPDF cannot read the pixels back out. It is left unset for the same-origin logo,
   because requesting CORS from our own host adds a preflight for nothing.

   A crossOrigin request gets a cache-busting query param the plain <img> thumbnails elsewhere in
   the app never send. Reason: the service worker's storage-photos route (service-worker.ts)
   caches Storage responses by URL, and the Cache API matches on URL alone — it does not
   distinguish an opaque (no-crossOrigin) response from a real CORS one. Every photo's thumbnail
   is shown, uncrossed, well before anyone opens the detailed report, so by the time this runs the
   cache almost always already holds an opaque entry for that URL; without a distinct key,
   CacheFirst would hand that opaque response back here too, taint the canvas, and rasterize()
   would silently drop the photo. Firebase Storage ignores unknown query params (confirmed against
   the live bucket), so `_cors=1` reaches the same object and gets cached under its own key,
   populated by an actual CORS-mode fetch — leaving the thumbnail's opaque entry untouched. */
export const loadImage = (src: string, crossOrigin?: 'anonymous' | 'use-credentials') =>
  new Promise<HTMLImageElement | null>(resolve => {
    const img = new Image();
    if (crossOrigin) {
      img.crossOrigin = crossOrigin;
      src += (src.includes('?') ? '&' : '?') + '_cors=1';
    }
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

/* Rasterize a decoded <img> onto a canvas, downscaled to `maxEdge` on its long side, for jsPDF.

   Why not hand jsPDF the <img> directly: it does NOT read pixels from an image element. It
   re-fetches img.src with a *synchronous* XMLHttpRequest and embeds the raw bytes — one blocking
   round trip per photo, and if that fetch fails (transient network, a Storage token that expired
   between the <img> load and the build) it throws and the whole PDF is lost. A canvas is the pixel
   path: jsPDF calls toDataURL on it, synchronously, from memory — nothing left to fail at build
   time. This is also where the download-size cap lives; uploads are already 1600px, so 1400 costs
   nothing visible on a letter page and keeps a 60-photo report reasonable.

   Returns null when the pixels cannot be read (a tainted bitmap — the CORS case — or no 2D
   context), so the caller can count it as missing rather than fail. */
export const rasterize = (img: HTMLImageElement, maxEdge = 1400): LoadedPhoto | null => {
  const scale = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight, 1));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  try {
    ctx.drawImage(img, 0, 0, width, height);
    // Touching the pixels is what surfaces a tainted (non-CORS) bitmap — fail here, not in jsPDF.
    ctx.getImageData(0, 0, 1, 1);
  } catch {
    return null;
  }
  return { image: canvas, width, height };
};

/** jsPDF accepts an <img>, a canvas or a data URI; the preview script passes a data URI. Prefer a
 *  canvas (see rasterize) — an <img> makes jsPDF re-fetch it with a synchronous XHR. */
export type ImageInput = HTMLImageElement | HTMLCanvasElement | string;
/** The letterhead is optional — a missing logo must not cost you the PDF. */
export type LogoInput = ImageInput | null;

export interface ReportPdfOptions {
  logo: LogoInput;
  /** Shown in the letterhead's "Prepared by" line. */
  preparedBy: string;
}

/** Suggested download name, derived from the project. */
export const reportFileName = (project: Project) =>
  `${project.name.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-') || 'project'}-report.pdf`;


export interface ReportChrome {
  logo: LogoInput;
  project: Project;
  preparedBy: string;
  /** Small-caps line above the project name. Distinguishes the two report tiers. */
  kicker: string;
  /** Overrides the default Address / Prepared by / Report date letterhead rows. The user guide
      is not project-scoped, so it supplies its own (Version / Updated / Applies to). */
  metaRows?: [string, string][];
  /** Draw the compact top-of-page letterhead. The detailed report turns this off and renders a
      full cover page instead (see detailedCoverPage). Default true. */
  letterhead?: boolean;
}

/* Document chrome shared by both report tiers: page geometry, the WinAnsi-safe text helpers, the
   branded autoTable defaults, the logo letterhead and the running head/footer. Section builders
   take the returned object and never construct any of it themselves, so the summary report and the
   detailed report cannot drift apart visually. */
export function startReport({ logo, project, preparedBy, kicker, metaRows, letterhead = true }: ReportChrome) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const m = 15;
  const cw = pw - m * 2;
  const TOP = 26;             // first baseline on a continuation page, clear of the running head
  const BOTTOM = ph - 20;     // last baseline before the footer


  let y = 0;
  const fill = (c: RGB) => doc.setFillColor(c[0], c[1], c[2]);
  const ink = (c: RGB) => doc.setTextColor(c[0], c[1], c[2]);
  const finalY = () => (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;

  const checkPage = (needed = 20) => {
    if (y + needed > BOTTOM) { doc.addPage(); y = TOP; }
  };

  /* Every string reaching the page goes through winAnsi() — via write(), wrap() or table() —
     so no caller has to remember. */
  type TextOptions = Parameters<typeof doc.text>[3];
  const write = (text: string, x: number, yy: number, opts?: TextOptions) =>
    doc.text(winAnsi(text), x, yy, opts);
  const wrap = (text: string, width: number): string[] =>
    doc.splitTextToSize(winAnsi(text), width);

  /** Green title with a rule under it. Wraps, so a long building name doesn't overrun the margin. */
  const sectionHeading = (title: string) => {
    checkPage(24);
    ink(PDF.green);
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    const lines = wrap(title, cw);
    const lh = 13 * 0.45;
    lines.forEach((ln, i) => doc.text(ln, m, c.y + i * lh));
    const lastY = c.y + (lines.length - 1) * lh;
    fill(PDF.green);
    doc.rect(m, lastY + 2.2, cw, 0.6, 'F');
    c.y = lastY + 9;
    ink(PDF.slate); doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  };

  const subHeading = (title: string, size = 10.5) => {
    checkPage(14);
    ink(PDF.slate);
    doc.setFontSize(size); doc.setFont('helvetica', 'bold');
    const lines = wrap(title, cw);
    const lh = size * 0.45;
    lines.forEach((ln, i) => doc.text(ln, m, c.y + i * lh));
    c.y += (lines.length - 1) * lh + size * 0.5 + 1;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  };

  const body = (text: string, size = 9) => {
    doc.setFontSize(size); doc.setFont('helvetica', 'normal'); ink(PDF.slate);
    const lines = wrap(text, cw);
    checkPage(lines.length * (size * 0.45) + 2);
    doc.text(lines, m, c.y);
    c.y += lines.length * (size * 0.45) + 1.5;
  };

  const tableBase: Partial<UserOptions> = {
    theme: 'grid',
    margin: { left: m, right: m, top: TOP, bottom: 20 },
    styles: {
      fontSize: 8.5, cellPadding: 2, overflow: 'linebreak',
      textColor: PDF.slate, lineColor: PDF.hairline, lineWidth: 0.1,
    },
    headStyles: {
      fillColor: PDF.green, textColor: [255, 255, 255],
      fontStyle: 'bold', fontSize: 8.5, lineColor: PDF.green, lineWidth: 0.1,
    },
    alternateRowStyles: { fillColor: PDF.zebra },
  };

  const sanitizeRow = (row: RowInput): RowInput =>
    (row as CellInput[]).map(c => (typeof c === 'string' ? winAnsi(c) : c));

  /** autoTable with the report's shared styling, and c.y left at the bottom of the table. */
  const table = (opts: UserOptions) => {
    autoTable(doc, {
      ...tableBase,
      ...opts,
      styles: { ...tableBase.styles, ...opts.styles },
      headStyles: { ...tableBase.headStyles, ...opts.headStyles },
      head: opts.head?.map(sanitizeRow),
      body: opts.body?.map(sanitizeRow),
    });
    c.y = finalY();
  };

  /** Paints a whole body row in one of the memo's status tints. */
  const tintRows = (tintFor: (rowIndex: number) => RGB | undefined): CellHook => cell => {
    if (cell.section !== 'body') return;
    const tint = tintFor(cell.row.index);
    if (tint) cell.cell.styles.fillColor = tint;
  };

  /* `c.y` is the cursor every section advances. Exposed as an accessor so section builders can write
     `c.y += 12` while the helpers above keep mutating the same closure variable. */
  const c = {
    doc, m, cw, TOP, BOTTOM,
    get y() { return y; },
    set y(v: number) { y = v; },
    finalY, fill, ink, write, wrap, checkPage,
    sectionHeading, subHeading, body, table, tintRows,
  };

  // ── Letterhead ── (skipped when the caller draws its own cover page instead)
  if (letterhead) {
    c.y = 18;
    if (logo) {
      doc.addImage(logo, 'PNG', m, c.y, LOGO_W_MM, LOGO_H_MM);
      c.y += LOGO_H_MM + 6;
    } else {
      ink(PDF.green); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
      write('Sustainability-Pros', m, c.y + 6);
      c.y += 13;
    }
    fill(PDF.green); doc.rect(m, c.y, cw, 1.2, 'F');
    c.y += 9;

    ink(PDF.green); doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    write(kicker, m, c.y, { charSpace: 0.6 });
    c.y += 9;

    ink(PDF.slate); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    const nameLines = wrap(project.name, cw);
    doc.text(nameLines, m, c.y);
    c.y += nameLines.length * 7.5 + 3;

    doc.setFontSize(10);
    (metaRows ?? [
      ['Address', project.address || '—'],
      ['Prepared by', preparedBy],
      ['Report date', new Date().toLocaleDateString()],
    ] as [string, string][]).forEach(([k, v]) => {
      ink(PDF.muted); doc.setFont('helvetica', 'bold');
      write(k, m, c.y);
      ink(PDF.slate); doc.setFont('helvetica', 'normal');
      write(wrap(v, cw - 34)[0], m + 34, c.y);
      c.y += 5.5;
    });
    c.y += 8;
  }

  return c;
}

/** Running head from page 2 on, plus a footer on every page. Call last.
 *  `coverFirstPage` leaves page 1's foot to the cover (its own tagline band owns the bottom). */
export function finishReport(
  c: ReturnType<typeof startReport>,
  project: Project,
  { coverPages = 0 }: { coverPages?: number } = {},
) {
  const { doc, m, cw, ink, fill, write } = c;
  const ph = doc.internal.pageSize.getHeight();
  const pw = doc.internal.pageSize.getWidth();
  // ── Running head + footer on every page ──
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    if (i > coverPages) {
      ink(PDF.green); doc.setFontSize(7.5); doc.setFont('helvetica', 'bold');
      write('SUSTAINABILITY-PROS  \u00b7  MEP TOOLS', m, 13, { charSpace: 0.3 });
      fill(PDF.green); doc.rect(m, 15.5, cw, 0.4, 'F');
    }

    if (i <= coverPages) {
      // Each cover page draws its own header/footer, but leaves its revision/page-count line for
      // us — deferred until the real page count is known. Both cover pages register a position
      // (keyed by doc, since that's all a WeakMap can key on), so this is a list, not a single
      // value; draw whichever entries belong to this page.
      for (const pos of coverFooterReportId.get(doc) ?? []) {
        if (pos.page !== i) continue;
        ink(pos.ink); doc.setFontSize(7.5); doc.setFont('courier', 'normal');
        write(`Rev 1  ·  ${pageCount} page${pageCount === 1 ? '' : 's'}`, pos.x, pos.y, { align: pos.align });
      }
      continue;
    }

    fill(PDF.hairline); doc.rect(m, ph - 14, cw, 0.3, 'F');
    ink(PDF.muted); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    // Clip the project name to the space left of the page number so the two never collide.
    const pageLabel = `Page ${i} of ${pageCount}`;
    const nameMax = cw - doc.getTextWidth(winAnsi(pageLabel)) - 8;
    let footName = project.name;
    if (doc.getTextWidth(winAnsi(footName)) > nameMax) footName = `${doc.splitTextToSize(winAnsi(footName), nameMax - 3)[0]}…`;
    write(footName, m, ph - 9);
    write(pageLabel, pw - m, ph - 9, { align: 'right' });
  }
}

export interface CapitalPlanRow {
  system: string; assetId: string; description: string; location: string;
  age: number | null; rsl: number; rating?: RatingLabel;
}
/** Assets with a known remaining service life, soonest-to-replace first — the core capital-planning
 *  output. Only assets that carry an RSL are included; you can't schedule a replacement you haven't
 *  dated. Shared by the PDF and the on-screen summary so the two can't drift. */
export function capitalPlanRows(fa: FacilityAssessment): CapitalPlanRow[] {
  const rows: CapitalPlanRow[] = [];
  SYSTEM_KEYS.forEach(k => fa[k].assets.forEach(a => {
    if (a.remainingServiceLife == null) return;
    rows.push({
      system: SYSTEM_LABELS[k], assetId: a.assetId || '—', description: a.description || '—',
      location: a.location || '—', age: a.age, rsl: a.remainingServiceLife, rating: a.rating,
    });
  }));
  return rows.sort((x, y) => x.rsl - y.rsl);
}

/** Per-system scores, recommendations and asset tables. The summary tier. */
export function facilitySection(c: ReturnType<typeof startReport>, data: ReportData) {
  // ── Facility Assessments ──
  if (data.facilityAssessments.length > 0) {
    c.sectionHeading('Facility Asset Assessments');

    data.facilityAssessments.forEach(fa => {
      c.checkPage(30);
      c.subHeading(fa.buildingName || 'Untitled Assessment', 12);
      const overallMax = maxPossibleScore(fa);
      const overallRating = getBuildingRating(fa.overallScore ?? 0, overallMax);
      c.body((overallMax > 0
        ? `Overall score: ${Math.round((fa.overallScore ?? 0) * 10) / 10} / ${overallMax}  —  ${overallRating}`
        : 'Overall: Not assessed')
        + (fa.assessmentDate ? `      Assessed: ${fa.assessmentDate}` : ''), 10);
      c.y += 3;

      c.table({
        startY: c.y,
        head: [['System', 'Score', 'Rating', 'Assets']],
        body: SYSTEM_KEYS.map(k => [
          SYSTEM_LABELS[k],
          fa[k].score != null ? Math.round((fa[k].score ?? 0) * 10) / 10 : '—',
          fa[k].rating ?? '—',
          fa[k].assets.length,
        ]),
        styles: { fontSize: 9 },
        headStyles: { fontSize: 9 },
        columnStyles: { 1: { halign: 'right' }, 3: { halign: 'right' } },
      });
      c.y += 7;

      SYSTEM_KEYS.forEach(k => {
        const sys = fa[k];
        if (sys.assets.length === 0) return;
        c.checkPage(24);
        c.subHeading(`${SYSTEM_LABELS[k]} Assets`);
        c.table({
          startY: c.y,
          head: [['ID', 'Description', 'Location', 'Age', 'RSL', 'Visual', 'Operational', 'Score', 'Rating']],
          body: sys.assets.map(a => [
            a.assetId || '—',
            a.description || '—',
            a.location || '—',
            a.age != null ? `${a.age}yr` : '—',
            a.remainingServiceLife != null ? `${a.remainingServiceLife}yr` : '—',
            a.visualInspection ?? '—',
            a.operationalTest ?? '—',
            isAssetAssessed(a) ? Math.round((a.score ?? 0) * 10) / 10 : '—',
            a.rating ?? '—',
          ]),
          styles: { fontSize: 7 },
          headStyles: { fontSize: 7 },
          /* Fixed widths summing to the 185.9mm content width, so the nine columns fill the
             page instead of huddling on the left. Description and Location get the slack —
             they're the two that wrap. */
          columnStyles: {
            0: { cellWidth: 13 }, 1: { cellWidth: 44 }, 2: { cellWidth: 29 },
            3: { cellWidth: 11, halign: 'right' }, 4: { cellWidth: 11, halign: 'right' },
            5: { cellWidth: 24 }, 6: { cellWidth: 24 },
            7: { cellWidth: 12, halign: 'right', fontStyle: 'bold' }, 8: { cellWidth: 17.9 },
          },
        });
        c.y += 7;
      });

      // ── Findings & Recommendations ── (generateRecommendations always returns at least one line)
      const recs = generateRecommendations(fa);
      c.checkPage(20);
      c.subHeading('Findings & Recommendations');
      recs.forEach(r => c.body(`•  ${r}`, 9));
      c.y += 3;

      // ── Capital planning, soonest replacement first ──
      const cap = capitalPlanRows(fa);
      if (cap.length) {
        c.checkPage(24);
        c.subHeading('Capital Planning — by Remaining Service Life');
        c.table({
          startY: c.y,
          head: [['RSL', 'System', 'ID', 'Description', 'Location', 'Age', 'Rating']],
          body: cap.map(r => [
            `${r.rsl}yr`, r.system, r.assetId, r.description, r.location,
            r.age != null ? `${r.age}yr` : '—', r.rating ?? '—',
          ]),
          styles: { fontSize: 7 },
          headStyles: { fontSize: 7 },
          columnStyles: {
            0: { cellWidth: 13, halign: 'right', fontStyle: 'bold' },
            1: { cellWidth: 22 }, 2: { cellWidth: 16 }, 3: { cellWidth: 63 },
            4: { cellWidth: 34 }, 5: { cellWidth: 12, halign: 'right' }, 6: { cellWidth: 25.9 },
          },
        });
        c.y += 7;
      }

      c.y += 4;
    });
  }

}

/* -- Deferred sections ---------------------------------------------------------------------
   Both report tiers are scoped to the facility assessment for now. These build the other
   modules' sections and are kept, compiled and type-checked, because that scoping is a staging
   decision rather than a removal: re-adding a module is one entry in the composition below.
   Do not delete them. */

/** Six headline figures across all modules. Deferred. */
export function executiveSummarySection(c: ReturnType<typeof startReport>, data: ReportData) {
  const s = summarize(data);
  // ── Executive summary ──
  const summaryCells: [string, string][] = [
    ['Assessments', String(data.facilityAssessments.length)],
    ['Active PM', String(s.activePM.length)],
    ['Overdue PM', String(s.overduePM.length)],
    ['Open WOs', String(s.openWOs.length)],
    ['Compliance', `${s.complianceHealthScore}%`],
    ['Overdue Comp.', String(s.overdueCompliance.length)],
  ];
  c.sectionHeading('Executive Summary');
  c.table({
    startY: c.y,
    head: [summaryCells.map(c => c[0])],
    body: [summaryCells.map(c => c[1])],
    headStyles: { halign: 'center', fontSize: 8 },
    styles: { halign: 'center', fontSize: 13, fontStyle: 'bold', cellPadding: 3.5 },
    alternateRowStyles: {},
  });
  c.y += 12;

}

/** Duct air leakage tests. Deferred. */
export function daltSection(c: ReturnType<typeof startReport>, data: ReportData) {
  // ── DALT ──
  if (data.daltResults.length > 0) {
    c.sectionHeading('Duct Air Leakage Tests (DALT)');
    const dalt = data.daltResults;
    c.table({
      startY: c.y,
      head: [['Section', 'Test Pressure', 'LC', 'Surface Area (ft²)', 'Actual (CFM)', 'Allowable (CFM)', '% Allow.', 'Result']],
      body: dalt.map(r => [
        r.sectionLabel || '—',
        `${r.testParameters.testPressure}"WC`,
        r.testParameters.leakageClass,
        r.testResults ? (r.testResults.surfaceArea / 144).toFixed(1) : '—',
        r.testResults ? r.testResults.actualLeakage.toFixed(2) : '—',
        r.testResults ? r.testResults.allowableLeakage.toFixed(2) : '—',
        r.testResults ? r.testResults.percentOfAllowable.toFixed(1) + '%' : '—',
        r.testResults ? (r.testResults.passed ? 'PASS' : 'FAIL') : '—',
      ]),
      columnStyles: { 1: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { fontStyle: 'bold' } },
      didParseCell: c.tintRows(i => (dalt[i].testResults && !dalt[i].testResults!.passed ? PDF.rowDanger : undefined)),
    });
    c.y += 12;
  }

}

/** Flow sensor calibrations. Deferred. */
export function ebtronSection(c: ReturnType<typeof startReport>, data: ReportData) {
  // ── Ebtron ──
  if (data.ebtronResults.length > 0) {
    c.sectionHeading('Flow Sensor Calibrations (Ebtron)');
    const ebtron = data.ebtronResults;
    c.table({
      startY: c.y,
      head: [['Sensor', 'Gain', 'Offset CFM', 'Offset FPM', 'Midrange Accuracy', 'Pass?', 'Date']],
      body: ebtron.map(r => [
        r.sensorLabel || '—',
        r.gain.toFixed(6),
        r.offsetCfm.toFixed(2),
        r.offsetFpm.toFixed(2),
        r.midrangeAccuracyPct != null ? r.midrangeAccuracyPct.toFixed(2) + '%' : '—',
        r.midrangePass != null ? (r.midrangePass ? 'Pass' : 'Fail') : '—',
        new Date(r.createdAt).toLocaleDateString(),
      ]),
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { fontStyle: 'bold' } },
      didParseCell: c.tintRows(i => (ebtron[i].midrangePass === false ? PDF.rowDanger : undefined)),
    });
    c.y += 12;
  }

}

/** Electrical power calculations. Deferred. */
export function powerSection(c: ReturnType<typeof startReport>, data: ReportData) {
  // ── Power ──
  if (data.powerResults.length > 0) {
    c.sectionHeading('Electrical Power Calculations');
    c.table({
      startY: c.y,
      head: [['Load Type', 'Solve For', 'Result', 'Notes', 'Date']],
      body: data.powerResults.map(r => [
        LOAD_LABELS[r.loadType] ?? r.loadType,
        r.mode,
        r.resultAmperage != null ? `${r.resultAmperage.toFixed(2)} A`
          : r.resultVoltage != null ? `${r.resultVoltage.toFixed(2)} V`
          : r.resultKw != null ? `${r.resultKw.toFixed(3)} kW` : '—',
        r.notes || '—',
        new Date(r.createdAt).toLocaleDateString(),
      ]),
      columnStyles: { 2: { halign: 'right', fontStyle: 'bold' } },
    });
    c.y += 12;
  }

}

/** Boiler ECM analysis. Deferred. */
export function boilerSection(c: ReturnType<typeof startReport>, data: ReportData) {
  // ── Boiler ECM ──
  if (data.boilerECMResults.length > 0) {
    c.sectionHeading('Boiler ECM Analysis');
    c.table({
      startY: c.y,
      head: [['Label', 'Fuel Type', 'Efficiency', 'Annual Cost (Max)', 'ECM Savings (Mid)', 'Active ECMs', 'Date']],
      body: data.boilerECMResults.map(r => {
        const ecms = [
          r.ecm1ReducedHoursPerDay > 0 && 'ROH',
          r.ecm2ImprovedEfficiencyPct > 0 && 'IBE',
          r.ecm3LoadReductionPct > 0 && 'HLR',
          r.ecm4ReducedDaysPerYear > 0 && 'ODR',
        ].filter(Boolean).join(', ');
        return [
          r.label || '—',
          r.fuelType,
          `${r.boilerEfficiencyPct}%`,
          `$${Math.round(r.annualCostAtMax).toLocaleString()}`,
          ecms ? `$${Math.round(r.savingsMidRange).toLocaleString()}` : '—',
          ecms || 'None',
          new Date(r.createdAt).toLocaleDateString(),
        ];
      }),
      columnStyles: {
        0: { fontStyle: 'bold' }, 2: { halign: 'right' },
        3: { halign: 'right' }, 4: { halign: 'right', fontStyle: 'bold' },
      },
    });
    c.y += 10;

    data.boilerECMResults.forEach(r => {
      c.checkPage(48);
      c.subHeading(r.label || `Boiler (${r.fuelType})`);
      c.body(`Nameplate: ${r.nameplateInputMmbtuh} MMBTUH input / ${r.nameplateOutputMmbtuh} MMBTUH output   |   ${r.serviceType}   |   ${r.constructionType}   |   ${r.flueType}`, 8.5);
      c.body(`Operating: ${r.operatingDaysPerYear} days/yr × ${r.operatingHoursPerDay} hrs/day = ${r.runTimeHours} hrs   |   Efficiency: ${r.boilerEfficiencyPct}%   |   Fuel: $${r.fuelCostPerTherm}/therm`, 8.5);
      c.y += 3;
      c.table({
        startY: c.y,
        head: [['Firing Rate', 'Current Annual Cost', 'ECM Annual Cost', 'Savings']],
        body: [
          [`Min Load (${r.minConnectedLoadMmbtuh} MMBTUH)`, `$${Math.round(r.annualCostAtMin).toLocaleString()}`, `$${Math.round(r.ecmAnnualCostAtMin).toLocaleString()}`, `$${Math.round(r.savingsAtMin).toLocaleString()}`],
          [`Selected (${r.variableFiringRatePct}%)`, `$${Math.round(r.annualCostAtSelected).toLocaleString()}`, `$${Math.round(r.ecmAnnualCostAtSelected).toLocaleString()}`, '—'],
          [`Max Load (${r.maxConnectedLoadMmbtuh} MMBTUH)`, `$${Math.round(r.annualCostAtMax).toLocaleString()}`, `$${Math.round(r.ecmAnnualCostAtMax).toLocaleString()}`, `$${Math.round(r.savingsAtMax).toLocaleString()}`],
          ['Mid-Range Savings', '', '', `$${Math.round(r.savingsMidRange).toLocaleString()} (${r.savingsPctAtMax.toFixed(1)}%)`],
        ],
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right', fontStyle: 'bold' } },
        didParseCell: c.tintRows(i => (i === 1 ? PDF.rowMuted : i === 3 ? PDF.rowTotal : undefined)),
      });
      c.y += 8;
    });
    c.y += 4;
  }

}

/** PM schedule summary. Deferred. */
export function pmSection(c: ReturnType<typeof startReport>, data: ReportData) {
  const s = summarize(data);
  // ── PM Schedule ──
  if (s.activePM.length > 0) {
    c.sectionHeading('PM Schedule Summary');
    const shown = s.activePM.slice(0, 20);
    c.table({
      startY: c.y,
      head: [['Task', 'Asset', 'Trade', 'Frequency', 'Next Due', 'Status']],
      body: shown.map(p => [
        p.taskName,
        p.assetDescription,
        p.trade,
        `${p.frequencyValue} ${p.frequencyUnit}`,
        p.nextDueDate,
        p.nextDueDate < s.today ? 'Overdue' : 'Scheduled',
      ]),
      columnStyles: { 4: { fontStyle: 'bold' } },
      didParseCell: c.tintRows(i => (shown[i].nextDueDate < s.today ? PDF.rowDanger : undefined)),
    });
    c.y += 4;
    if (s.activePM.length > shown.length) {
      c.ink(PDF.muted); c.doc.setFontSize(8); c.doc.setFont('helvetica', 'italic');
      c.write(`+ ${s.activePM.length - shown.length} more tasks \u2014 see the full PM Scheduler module`, c.m, c.y);
      c.doc.setFont('helvetica', 'normal');
      c.y += 5;
    }
    c.y += 8;
  }

}

/** Compliance summary. Deferred. */
export function complianceSection(c: ReturnType<typeof startReport>, data: ReportData) {
  const s = summarize(data);
  // ── Compliance ──
  if (data.complianceRecords.length > 0) {
    c.sectionHeading('Compliance Summary');
    c.body(`Compliance health: ${s.complianceHealthScore}%`
      + (s.overdueCompliance.length > 0 ? `      ${s.overdueCompliance.length} overdue` : ''), 10);
    c.y += 3;
    const shown = data.complianceRecords.slice(0, 50);
    c.table({
      startY: c.y,
      head: [['Standard', 'Task', 'Asset', 'Last Completed', 'Next Due', 'Cert', 'Status']],
      body: shown.map(r => [
        r.standard,
        r.taskName,
        r.assetDescription,
        r.lastCompletedDate || '—',
        r.nextDueDate,
        r.certificateFiled ? 'Yes' : '—',
        r.status === 'due-soon' ? 'Due Soon' : r.status.charAt(0).toUpperCase() + r.status.slice(1),
      ]),
      columnStyles: { 0: { fontStyle: 'bold' }, 4: { fontStyle: 'bold' }, 5: { halign: 'center' } },
      didParseCell: c.tintRows(i =>
        shown[i].status === 'overdue' ? PDF.rowDanger
          : shown[i].status === 'due-soon' ? PDF.rowWarn
            : undefined),
    });
    c.y += 4;
    if (data.complianceRecords.length > shown.length) {
      c.ink(PDF.muted); c.doc.setFontSize(8); c.doc.setFont('helvetica', 'italic');
      c.write(`+ ${data.complianceRecords.length - shown.length} more records`, c.m, c.y);
      c.doc.setFont('helvetica', 'normal');
    }
  }

}

/** Sections the summary tier could carry, in print order. None are composed today — add one to
 *  `buildReportPdf` to bring that module back into the report. */
export const DEFERRED_SECTIONS = [
  executiveSummarySection, daltSection, ebtronSection,
  powerSection, boilerSection, pmSection, complianceSection,
];

/** The summary report. Scoped to the facility assessment. */
export function buildReportPdf(data: ReportData, { logo, preparedBy }: ReportPdfOptions): jsPDF {
  const c = startReport({
    logo, project: data.project, preparedBy, kicker: 'FACILITY ASSESSMENT - SUMMARY',
  });
  facilitySection(c, data);
  finishReport(c, data.project);
  return c.doc;
}


/* ── Detailed report ─────────────────────────────────────────────────────────────────────────
   Paul's Asset Assessment Score Card, one per asset, with the photo evidence on the same card.
   The layout follows the workbook tab: identity block, the six condition selections with their
   points, the forced-downgrade banner, then score / rating / condition sentence. Everything is
   read from the same functions the app scores with, so a card cannot disagree with the module. */

/** A photo already decoded by the caller. jsPDF cannot fetch, and it needs the intrinsic size to
 *  avoid stretching, so loading stays in the browser and the builder stays synchronous. */
export interface LoadedPhoto {
  image: ImageInput;
  width: number;
  height: number;
}
/** Keyed by FacilityPhoto.id. Photos absent from the map are skipped, not drawn as gaps. */
export type PhotoMap = Record<string, LoadedPhoto | undefined>;

export interface DetailedReportOptions extends ReportPdfOptions {
  photos?: PhotoMap;
  /** Mark-only image for page 2's cover brand lockup (see MARK_SRC). Falls back to drawing no
      mark image (text only) if omitted. */
  mark?: LogoInput;
  /** White/mono mark for page 1's dark left panel (see MARK_WHITE_SRC). Same fallback as mark. */
  markWhite?: LogoInput;
  /** A labeled site/aerial photo identifying which building this project covers (Project.
      sitePhotoUrl) — shown on the title page. Needs pixel dimensions (unlike mark/markWhite,
      which are drawn at a fixed size) so its aspect ratio is preserved when fit into the
      available space, hence LoadedPhoto rather than plain LogoInput. Omitted when the project
      has none. */
  sitePhoto?: LoadedPhoto;
}

/* Exported so the on-screen detailed report (ReportDetailedView) renders the exact same
   selection/notes the PDF does — the two detailed reports read from one source. */
export const CRITERION_VALUE = (asset: FacilityAsset, key: CriterionKey): string => {
  switch (key) {
    case 'age': return asset.age != null ? `${asset.age} yr` : '—';
    case 'remainingServiceLife': return asset.remainingServiceLife != null ? `${asset.remainingServiceLife} yr` : '—';
    default: return String(asset[key] ?? '—');
  }
};

/** criterionNotes is the current home; the two deprecated fields are still read for old records. */
export const CRITERION_NOTE = (asset: FacilityAsset, key: CriterionKey): string => {
  const note = asset.criterionNotes?.[key];
  if (note) return note;
  if (key === 'visualInspection' && asset.visualIssues) return asset.visualIssues;
  if (key === 'operationalTest' && asset.operationalIssues) return asset.operationalIssues;
  return '';
};

/* Estimates the height of a card's core block (title through the score row — the part that used
   to get cut apart mid-table) so checkPage() can move the WHOLE thing to a fresh page rather than
   let autoTable split the criteria table wherever it runs out of room. This approximates
   autoTable's own row-height math (fontSize 8, 2mm cell padding each side, ~3.5mm per wrapped
   line) instead of reverse-engineering it exactly — a slight overestimate just costs a little
   blank space at a page foot, which is the trade Paul asked for; an underestimate is the bug this
   replaces. Kept in sync with assetCard's own layout below if that ever changes. */
function estimateCardHeight(c: ReturnType<typeof startReport>, asset: FacilityAsset): number {
  const { cw, wrap } = c;
  const ROW_H = 8.5;
  const EXTRA_LINE_H = 3.5;
  let h = 9; // green rule + title line

  // Info table: two rows, each the taller of its two cells (Description/Model-Serial are the
  // only ones realistically long enough to wrap).
  const cellLines = (text: string, width: number) => Math.max(1, wrap(text, width).length);
  h += Math.max(
    cellLines(asset.location || '—', 68), cellLines(asset.manufacturer || '—', cw - 118),
  ) * ROW_H;
  h += Math.max(
    cellLines(asset.description || '—', 68),
    cellLines([asset.model, asset.serialNumber].filter(Boolean).join('  /  ') || '—', cw - 118),
  ) * ROW_H;
  h += 5;

  // Criteria table: header + one row per criterion, each possibly wrapped on its note.
  const { rows } = getCriterionPoints(asset);
  const noteW = cw - 108;
  h += ROW_H; // header
  h += rows.reduce((sum, r) => {
    const noteLines = cellLines(CRITERION_NOTE(asset, r.key) || '—', noteW);
    return sum + ROW_H + (noteLines - 1) * EXTRA_LINE_H;
  }, 0);
  h += 4;

  if (getDowngradeNote(asset)) h += 13.5;

  h += 6 + 10; // score row + its trailing gap

  if (asset.notes?.trim()) h += 3.5 + cellLines(asset.notes, cw) * 4.2 + 2;   // GENERAL NOTES block
  return h;
}

function assetCard(
  c: ReturnType<typeof startReport>,
  asset: FacilityAsset,
  systemLabel: string,
  photos: PhotoMap,
) {
  const { m, cw } = c;
  const assessed = isAssetAssessed(asset);
  const score = Math.round((asset.score ?? 0) * 10) / 10;
  const rating = asset.rating ?? 'Average';

  // Move the whole card to a fresh page rather than let autoTable split the criteria table
  // wherever it runs out of room — see estimateCardHeight for why a flat number isn't enough.
  // Capped at one full page's height: checkPage adds a page whenever the request doesn't fit
  // from the CURRENT y, so an uncapped estimate for an unusually tall card (lots of wrapped
  // notes) would force a spurious blank page even when already sitting at the top of one.
  /* Start the card wherever its header, tables and notes fit whole; photo rows may flow onto the
     next page under a "continued — <asset>" tag (drawn below). Reserving room for the first photo
     row too was tried and rejected: it left pages that were half to nine-tenths empty and added
     ~6 pages to a 43-page report. */
  c.checkPage(Math.min(estimateCardHeight(c, asset), c.BOTTOM - c.TOP));

  c.fill(PDF.green);
  c.doc.rect(m, c.y - 4, cw, 0.6, 'F');
  c.y += 3;

  // Measure the right-aligned system label first, then clip the bold title to the space it leaves,
  // so a long "ID — description" can't run under the label or past the right margin.
  c.doc.setFontSize(8.5); c.doc.setFont('helvetica', 'normal');
  const labelW = c.doc.getTextWidth(winAnsi(systemLabel));
  c.ink(PDF.slate);
  c.doc.setFontSize(12); c.doc.setFont('helvetica', 'bold');
  let cardTitle = `${asset.assetId || '(no ID)'} — ${asset.description || 'No description'}`;
  const titleAvail = cw - labelW - 6;
  if (c.doc.getTextWidth(winAnsi(cardTitle)) > titleAvail) cardTitle = `${c.wrap(cardTitle, titleAvail - 3)[0]}…`;
  c.write(cardTitle, m, c.y);
  c.ink(PDF.muted);
  c.doc.setFontSize(8.5); c.doc.setFont('helvetica', 'normal');
  c.write(systemLabel, m + cw, c.y, { align: 'right' });
  c.y += 6;

  c.table({
    startY: c.y,
    body: [
      ['Location', asset.location || '—', 'Manufacturer', asset.manufacturer || '—'],
      ['Description', asset.description || '—', 'Model / Serial',
       [asset.model, asset.serialNumber].filter(Boolean).join('  /  ') || '—'],
    ],
    styles: { fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 24, fontStyle: 'bold', textColor: PDF.muted },
      1: { cellWidth: 68 },
      2: { cellWidth: 26, fontStyle: 'bold', textColor: PDF.muted },
      3: { cellWidth: cw - 118 },
    },
  });
  c.y += 5;

  // ── The six condition selections, as the workbook lays them out ──
  const { rows, pinned } = getCriterionPoints(asset);
  c.table({
    startY: c.y,
    head: [['Assessment Condition Selection', 'Selection', 'Points', 'Max', 'Notes and Comments']],
    body: rows.map(r => [
      r.label + (r.band !== undefined ? ` (scored as the ${r.band} yr band)` : ''),
      CRITERION_VALUE(asset, r.key),
      pinned ? '—' : String(r.points),
      String(r.max),
      CRITERION_NOTE(asset, r.key) || '—',
    ]),
    styles: { fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 52 }, 1: { cellWidth: 26 },
      2: { cellWidth: 16, halign: 'right' }, 3: { cellWidth: 14, halign: 'right' },
      4: { cellWidth: cw - 108 },
    },
    // Belt-and-suspenders: estimateCardHeight (above) should keep this from ever running off a
    // page, but if a card is genuinely too tall for one page (unusually long wrapped notes) and
    // it still splits, repeat the asset's identity on the continuation page rather than leaving
    // a bare table header with no context.
    didDrawPage: (data: HookData) => {
      const startPage = (data.table as { startPageNumber?: number }).startPageNumber;
      if (startPage == null || data.pageNumber <= startPage) return;
      c.ink(PDF.muted); c.doc.setFontSize(7.5); c.doc.setFont('helvetica', 'italic');
      c.write(`continued — ${cardTitle}`, m, 21);
      c.doc.setFont('helvetica', 'normal');
    },
  });
  c.y += 4;

  /* Excel K24. When a forced downgrade fired, the six point values above never reached the total,
     so they are shown as “—” (struck through in the app) and this banner explains the score
     instead. The second line spells out what those struck-through points mean — none were
     awarded; the score comes from the downgrade, not the criteria — so the report says outright
     what the strikethrough only implies. */
  const downgrade = getDowngradeNote(asset);
  if (downgrade) {
    c.checkPage(16);
    c.fill(PDF.rowWarn);
    c.doc.rect(m, c.y - 3.5, cw, 11, 'F');
    c.ink(PDF.slate);
    c.doc.setFontSize(8.5); c.doc.setFont('helvetica', 'bold');
    c.write(downgrade, m + cw / 2, c.y + 1, { align: 'center' });
    c.ink(PDF.muted);
    c.doc.setFontSize(7.5); c.doc.setFont('helvetica', 'italic');
    c.write('No assessment points awarded — the score below is set by this downgrade, not the criteria above.',
      m + cw / 2, c.y + 5.5, { align: 'center' });
    c.doc.setFont('helvetica', 'normal'); c.doc.setFontSize(8.5); c.ink(PDF.slate);
    c.y += 13.5;
  }

  // ── Score / rating / condition sentence — the workbook's bottom row ──
  c.checkPage(14);
  c.table({
    startY: c.y,
    body: [[
      'Assessment Score', assessed ? String(score) : '—', 'Rating', assessed ? rating : 'Not assessed',
      assessed ? getAssetConditionText(asset, rating) : 'Not assessed',
    ]],
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 32, fontStyle: 'bold', fillColor: PDF.rowMuted },
      1: { cellWidth: 18, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 16, fontStyle: 'bold', fillColor: PDF.rowMuted },
      3: { cellWidth: 22, fontStyle: 'bold' },
      4: { cellWidth: cw - 88 },
    },
    didParseCell: cell => {
      if (cell.section !== 'body' || cell.column.index !== 3) return;
      const tint = rating === 'Failed' || rating === 'Critical' ? PDF.rowDanger
        : rating === 'Poor' ? PDF.rowWarn : undefined;
      if (tint) cell.cell.styles.fillColor = tint;
    },
  });
  c.y += 6;

  // ── General Notes — free-text, no Excel counterpart (FacilityAsset.notes); only when set,
  //    so an unused field never adds blank space to the card. ──
  if (asset.notes && asset.notes.trim()) {
    c.checkPage(10);
    c.doc.setFontSize(7.5); c.doc.setFont('helvetica', 'bold'); c.ink(PDF.muted);
    c.write('GENERAL NOTES', m, c.y);
    c.y += 3.5;
    c.body(asset.notes, 8.5);
    c.y += 2;
  }

  // ── Photo evidence, on the same card ──
  const shots = (asset.photos ?? []).map(ph => ({ meta: ph, loaded: photos[ph.id] }))
    .filter((x): x is { meta: FacilityPhoto; loaded: LoadedPhoto } => !!x.loaded);
  if (shots.length === 0) return;

  const GAP = 6;
  const boxW = (cw - GAP) / 2;
  // 48 mm (was 52): two spilled photo rows then leave room for a typical card's tables on the
  // same page, instead of a half-empty page. Still ~1.9 in tall on a letter page.
  const boxH = 48;
  for (let i = 0; i < shots.length; i += 2) {
    const pair = shots.slice(i, i + 2);
    const capLines = pair.map(s => s.meta.caption ? c.wrap(s.meta.caption, boxW).length : 0);
    const rowH = boxH + 4 + Math.max(0, ...capLines) * 3.6;
    // A photo row that lands on a fresh page gets the same "continued" tag the criteria table uses,
    // so a reader never meets a bare pair of photos with no asset name above them.
    const breaks = c.y + rowH + 4 > c.BOTTOM;
    c.checkPage(rowH + 4);
    if (breaks) {
      c.ink(PDF.muted); c.doc.setFontSize(7.5); c.doc.setFont('helvetica', 'italic');
      c.write(`continued — ${cardTitle}`, m, c.y);
      c.doc.setFont('helvetica', 'normal');
      c.y += 5;
    }
    pair.forEach((s, j) => {
      const x = m + j * (boxW + GAP);
      // Fit inside the box without stretching — jsPDF scales to whatever w/h it is handed.
      const scale = Math.min(boxW / s.loaded.width, boxH / s.loaded.height);
      const w = s.loaded.width * scale;
      const h = s.loaded.height * scale;
      try {
        c.doc.addImage(s.loaded.image, 'JPEG', x + (boxW - w) / 2, c.y, w, h);
      } catch (err) {
        // One bad bitmap must not cost the report: leave a marker in the box and carry on.
        console.warn('Detailed PDF: could not embed photo', s.meta.id, err);
        c.ink(PDF.muted); c.doc.setFontSize(7.5); c.doc.setFont('helvetica', 'italic');
        c.doc.text('Photo unavailable', x + 2, c.y + boxH / 2);
      }
      if (s.meta.caption) {
        c.ink(PDF.muted);
        c.doc.setFontSize(7.5); c.doc.setFont('helvetica', 'normal');
        c.doc.text(c.wrap(s.meta.caption, boxW), x, c.y + boxH + 4);
      }
    });
    c.y += rowH + 4;
  }
  c.y += 2;
}

/* Title page, page 1 — "Split panel" (option 1b of Sustainability-Pros Title Page Options.html):
   brand-forward, full-bleed. A dark green left panel (mark, wordmark, tagline, then — pinned
   near its bottom — the report ID/revision and contact info) beside a white right panel
   (eyebrow, building name, address, optional "Prepared for [client]", then a Scope & Dates list).
   Wordmark stays green/charcoal on the white panel (matches page 2 and everything else already
   shipped); the left panel needs the white/mono mark (markWhite) since the full-colour mark's
   pale tints don't read on a dark ground — same reasoning as the AppShell dark sider. */
function splitPanelTitlePage(
  c: ReturnType<typeof startReport>,
  data: ReportData,
  preparedBy: string,
  markWhite: LogoInput,
  sitePhoto?: LoadedPhoto,
) {
  const { doc } = c;
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();

  const pad = 14;
  const leftW = pw * 0.42;
  c.fill(PDF.panelGreen); doc.rect(0, 0, leftW, ph, 'F');

  // ── Left panel: brand lockup, then (pinned near the bottom) report ID + contact info ──
  const markSize = 18;
  let ly = pad + 4;
  if (markWhite) doc.addImage(markWhite, 'PNG', pad, ly, markSize, markSize);
  ly += markSize + 7;
  // One line: split across two lines at the hyphen it read as an accidental wrap. Sized to fit the
  // panel (16 pt fits comfortably; the loop only matters if the panel proportions change).
  doc.setFont('helvetica', 'bold');
  let wordmarkPt = 16;
  doc.setFontSize(wordmarkPt);
  while (wordmarkPt > 11 && doc.getTextWidth('Sustainability-Pros') > leftW - pad * 2) {
    wordmarkPt -= 0.5; doc.setFontSize(wordmarkPt);
  }
  c.ink(PDF.white); c.write('Sustainability-Pros', pad, ly);
  ly += 6.5;
  c.ink(PDF.lightGreen); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
  c.write('TAKING A CLOSER LOOK', pad, ly, { charSpace: 0.8 });

  const fas = data.facilityAssessments;
  const totalAssets = fas.reduce((n, fa) => n + assessedAssetCount(fa), 0);
  const systems = SYSTEM_KEYS.filter(key => fas.some(fa => fa[key].assets.length > 0)).map(key => SYSTEM_LABELS[key]);
  const dates = fas.map(fa => fa.assessmentDate).filter((d): d is string => !!d).sort();
  const assessedText = dates.length === 0 ? '—' : formatCoverDate(dates[0]);
  const reportDateText = formatCoverDate(new Date().toISOString().slice(0, 10));
  const reportId = deriveReportId(data.project.name, new Date());

  const footBlockH = 58;
  let fy = ph - pad - footBlockH;
  c.ink(PDF.lightGreen); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
  c.write('REPORT', pad, fy, { charSpace: 1 });
  fy += 6;
  c.ink(PDF.white); doc.setFontSize(11);
  c.write(reportId, pad, fy);
  fy += 5.5;
  c.ink(PDF.lightGreen); doc.setFont('courier', 'normal'); doc.setFontSize(8.5);
  // "Rev 1 · N pages" is deferred to finishReport(), which knows the true page count once every
  // page exists; this page is always page 1.
  registerCoverFooterPos(c.doc, 1, pad, fy, 'left', PDF.lightGreen);
  fy += 10;
  c.ink(PDF.lightGreen); doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
  c.write('3036C Piney Grove Church Road', pad, fy); fy += 4.3;
  c.write('Siler City, NC 27344', pad, fy); fy += 6.5;
  c.write('919-930-5314', pad, fy); fy += 4.3;
  c.write('info@sustainability-pros.com', pad, fy); fy += 4.3;
  c.write('www.sustainability-pros.com', pad, fy);

  // ── Right panel: title block, then a Scope & Dates list ──
  const rightX = leftW + pad;
  const rightW = pw - rightX - pad;
  let ry = pad + 4;
  c.ink(PDF.green); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  c.write('FACILITY CONDITION ASSESSMENT', rightX, ry, { charSpace: 1.3 });
  ry += 11;

  // Up to two lines, shrinking from 24pt only if it still doesn't fit at that height.
  c.ink(PDF.slate); doc.setFont('helvetica', 'bold');
  let heroSize = 24;
  doc.setFontSize(heroSize);
  let heroLines = c.wrap(winAnsi(data.project.name), rightW);
  while (heroLines.length > 2 && heroSize > 15) {
    heroSize -= 1; doc.setFontSize(heroSize);
    heroLines = c.wrap(winAnsi(data.project.name), rightW);
  }
  heroLines.slice(0, 2).forEach(ln => { c.write(ln, rightX, ry); ry += heroSize * 0.42; });
  ry += 2;

  if (data.project.address) {
    c.ink(PDF.coverInk); doc.setFontSize(11); doc.setFont('helvetica', 'normal');
    c.write(data.project.address, rightX, ry);
    ry += 9;
  }

  if (data.project.client) {
    c.ink(PDF.coverInk); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    c.write('PREPARED FOR', rightX, ry, { charSpace: 1 });
    const labelW = doc.getTextWidth('PREPARED FOR') + 1 * ('PREPARED FOR'.length - 1) + 5;
    c.ink(PDF.slate); doc.setFontSize(13);
    c.write(data.project.client, rightX + labelW, ry + 0.3);
    ry += 9;
  }
  ry += 4;
  doc.setDrawColor(...PDF.coverBorder); doc.setLineWidth(0.3);
  doc.line(rightX, ry, rightX + rightW, ry);
  ry += 10;

  c.ink(PDF.green); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
  c.write('SCOPE & DATES', rightX, ry, { charSpace: 1 });
  ry += 2.5;
  doc.line(rightX, ry, rightX + rightW, ry);
  ry += 7;

  const rows: [string, string][] = [
    ['Buildings assessed', String(fas.length)],
    ['Assets assessed', String(totalAssets)],
    ['Systems covered', systems.length ? systems.join(', ') : '—'],
    ['Assessment date', assessedText],
    ['Report date', reportDateText],
    ['Prepared by', preparedBy],
  ];
  doc.setFontSize(9.5);
  rows.forEach(([k, v]) => {
    c.ink(PDF.coverInk); doc.setFont('helvetica', 'normal');
    c.write(k, rightX, ry);
    c.ink(PDF.slate); doc.setFont('helvetica', 'bold');
    // "Systems covered" can run to a second line; the rest are short enough for one.
    const lines = c.wrap(v, rightW * 0.5).slice(0, 2);
    lines.forEach((ln, li) => c.write(ln, rightX + rightW, ry + li * 4, { align: 'right' }));
    ry += lines.length > 1 ? 11 : 8.5;
  });

  // ── Site photo: a labeled aerial/site shot identifying which building this covers, fit into
  // whatever space is left above the page's bottom margin, preserving its aspect ratio. ──
  if (sitePhoto) {
    ry += 8;
    const availH = ph - pad - ry;
    if (availH > 30) {
      c.ink(PDF.green); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
      c.write('SITE PHOTO', rightX, ry, { charSpace: 1 });
      ry += 2.5;
      doc.setDrawColor(...PDF.coverBorder); doc.setLineWidth(0.3);
      doc.line(rightX, ry, rightX + rightW, ry);
      ry += 6;

      const ratio = sitePhoto.height / sitePhoto.width;
      let drawW = rightW;
      let drawH = drawW * ratio;
      const remainingH = ph - pad - ry;
      if (drawH > remainingH) { drawH = remainingH; drawW = drawH / ratio; }
      doc.addImage(sitePhoto.image, 'JPEG', rightX, ry, drawW, drawH);
      doc.setDrawColor(...PDF.coverBorder); doc.setLineWidth(0.4);
      doc.rect(rightX, ry, drawW, drawH, 'S');
    }
  }
}

/** The detailed report: every assessed asset gets a score card, grouped by system. */
/* The redesigned, condition-focused cover — from the brand handoff's Report Cover reference.
   Left-aligned, top to bottom: brand lockup, eyebrow + building name + "Prepared for", a scored
   condition block, a Scope/Report column grid, and a footer (contact info left, report ID +
   revision + page count right — the last of which needs the final page count, so finishReport()
   fills it in once every page exists; see coverFooterReportId below).
   Drawn on page 2, right after splitPanelTitlePage(); the per-asset cards start on page 3. */
function detailedCoverPage(
  c: ReturnType<typeof startReport>,
  data: ReportData,
  preparedBy: string,
  mark: LogoInput,
) {
  const { doc, m, cw } = c;
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();

  // Top accent bar, bleeding across the full sheet.
  c.fill(PDF.green); doc.rect(0, 0, pw, 2.3, 'F');

  // ── Brand lockup: mark image + real text (wordmark, tagline) rather than the flattened
  // full-lockup raster, so the two-tone wordmark stays selectable text. ──
  const lockupY = 17;
  const textX = mark ? m + MARK_MM + 5 : m;
  if (mark) doc.addImage(mark, 'PNG', m, lockupY, MARK_MM, MARK_MM);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(17);
  c.ink(PDF.green); c.write('Sustainability-', textX, lockupY + 8);
  const nameW = doc.getTextWidth('Sustainability-');
  c.ink(PDF.slate); c.write('Pros', textX + nameW, lockupY + 8);
  c.ink(PDF.coverInk); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
  c.write('TAKING A CLOSER LOOK', textX, lockupY + 13.5, { charSpace: 0.7 });
  c.y = lockupY + MARK_MM + 6;

  // ── Title block: eyebrow, building name (the hero — largest element on the page), address,
  // then "Prepared for [client]" when the project has one. ──
  c.ink(PDF.green); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  c.write('FACILITY CONDITION ASSESSMENT', m, c.y, { charSpace: 1.3 });
  c.y += 10;

  // Auto-fit: the building name must not overflow the margin, so shrink from 26pt until it fits
  // on one line (wrapping a hero title reads as a mistake, not a design choice).
  c.ink(PDF.slate); doc.setFont('helvetica', 'bold');
  let heroSize = 26;
  doc.setFontSize(heroSize);
  while (doc.getTextWidth(winAnsi(data.project.name)) > cw && heroSize > 15) {
    heroSize -= 1; doc.setFontSize(heroSize);
  }
  c.write(data.project.name, m, c.y);
  c.y += heroSize * 0.42;

  if (data.project.address) {
    c.ink(PDF.coverInk); doc.setFontSize(10.5); doc.setFont('helvetica', 'normal');
    c.write(data.project.address, m, c.y);
    c.y += 6.5;
  }

  if (data.project.client) {
    c.y += 2;
    c.ink(PDF.coverInk); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    c.write('PREPARED FOR', m, c.y, { charSpace: 1 });
    const labelW = doc.getTextWidth(winAnsi('PREPARED FOR')) + 1 * 'PREPARED FOR'.length + 4;
    c.ink(PDF.slate); doc.setFontSize(11);
    c.write(data.project.client, m + labelW, c.y);
  }
  c.y += 10;

  // ── Condition score block: only meaningful for a single-building report (see the identical
  // constraint on the old summary card below). The 5-color bar (BAND_COLORS, keyed by
  // RATING_TO_BAND) is decoration only — a poor-to-excellent gradient with no words attached — so
  // it can never contradict the rating word itself. The headline label is the app's native
  // seven-tier RatingLabel, the same word used everywhere else in this report (page 3's per-building
  // header, every system/asset table): printing the 5-band name here too used to show a DIFFERENT
  // word for the same score one page apart (e.g. a "Poor" rating read "MARGINAL" on this cover). ──
  const fas = data.facilityAssessments;
  if (fas.length === 1) {
    const fa = fas[0];
    const overallMax = maxPossibleScore(fa);
    if (overallMax > 0) {
      const score = Math.round((fa.overallScore ?? 0) * 10) / 10;
      const rating = getBuildingRating(fa.overallScore ?? 0, overallMax);
      const colors = BAND_COLORS[RATING_TO_BAND[rating]];

      const boxH = 30;
      const leftW = 62;
      doc.setDrawColor(...PDF.coverBorder); doc.setLineWidth(0.3);
      doc.roundedRect(m, c.y, cw, boxH, 2, 2, 'S');

      // Left cell: score + rating, tinted to its severity color.
      c.fill(colors.bg); doc.rect(m + 0.3, c.y + 0.3, leftW - 0.6, boxH - 0.6, 'F');
      doc.setDrawColor(...PDF.coverBorder); doc.line(m + leftW, c.y, m + leftW, c.y + boxH);
      c.ink(colors.ink); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
      c.write('OVERALL CONDITION', m + 6, c.y + 9, { charSpace: 0.8 });
      doc.setFontSize(21);
      c.write(String(score), m + 6, c.y + 19);
      const scoreW = doc.getTextWidth(winAnsi(String(score)));
      doc.setFontSize(9.5); doc.setFont('helvetica', 'normal');
      c.write(` / ${overallMax}`, m + 6 + scoreW, c.y + 19);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
      c.write(rating.toUpperCase(), m + 6, c.y + 25.5, { charSpace: 0.6 });

      // Right cell: the 5-color gradient bar, a legend in the report's own seven-tier vocabulary,
      // and a caption. The legend is one running line of "tier range" tokens (wrapping if the
      // font/width ever demand it) rather than a word under each of the five segments: seven
      // words don't fit under five (or seven) segments at a legible size, and the old five-word
      // legend is exactly what put "MARGINAL" on this page over a "Poor" rating. Ranges are % of
      // the maximum, since a multi-system building scores out of 200–400, not 100.
      const rightX = m + leftW + 8;
      const rightW = cw - leftW - 16;
      const segY = c.y + 8;
      const segGap = 1;
      const segW = (rightW - segGap * 4) / 5;
      BAND_ORDER.forEach((b, i) => {
        c.fill(BAND_COLORS[b].bar);
        doc.rect(rightX + i * (segW + segGap), segY, segW, 3.2, 'F');
      });

      // Thresholds mirror getBuildingRating (facilityCalculations.ts).
      const TIERS: [RatingLabel, string][] = [
        ['Failed', '0–5%'], ['Critical', '5–10%'], ['Poor', '10–25%'], ['Average', '25–50%'],
        ['Good', '50–65%'], ['Like New', '65–80%'], ['New', '80–100%'],
      ];
      doc.setFontSize(6.5);
      const sep = '  ·  ';
      const tokenWidth = (tier: RatingLabel, range: string) => {
        doc.setFont('helvetica', tier === rating ? 'bold' : 'normal');
        return doc.getTextWidth(winAnsi(`${tier} ${range}`));
      };
      doc.setFont('helvetica', 'normal');
      const sepW = doc.getTextWidth(sep);
      let lx = rightX;
      let legendY = segY + 7;
      TIERS.forEach(([tier, range], i) => {
        const active = tier === rating;
        const tokenW = tokenWidth(tier, range);
        c.ink(active ? colors.ink : PDF.coverInk);
        doc.setFont('helvetica', active ? 'bold' : 'normal');
        c.write(`${tier} ${range}`, lx, legendY);
        lx += tokenW;
        if (i === TIERS.length - 1) return;
        // Wrap *before* the separator when the next token won't fit, so a line never ends in "·".
        const [nextTier, nextRange] = TIERS[i + 1];
        if (lx + sepW + tokenWidth(nextTier, nextRange) > rightX + rightW) { lx = rightX; legendY += 3.4; return; }
        c.ink(PDF.coverInk); doc.setFont('helvetica', 'normal'); c.write(sep, lx, legendY); lx += sepW;
      });

      const totalAssets = assessedAssetCount(fa);
      const downgraded = SYSTEM_KEYS.reduce((n, k) => n + fa[k].assets.filter(getDowngradeNote).length, 0);
      const caption = `Scored across ${totalAssets} asset${totalAssets === 1 ? '' : 's'}.`
        + (downgraded > 0 ? ` ${downgraded} unit${downgraded === 1 ? '' : 's'} downgraded for operational or visual issues.` : '');
      c.ink(PDF.coverInk); doc.setFontSize(8.5); doc.setFont('helvetica', 'normal');
      c.wrap(caption, rightW).slice(0, 2).forEach((ln, i) => c.write(ln, rightX, legendY + 6.5 + i * 4));

      c.y += boxH + 10;
    }
  }

  // ── Scope / Report two-column grid ──
  const totalAssets2 = fas.reduce((n, fa) => n + assessedAssetCount(fa), 0);
  const systems = SYSTEM_KEYS.filter(key => fas.some(fa => fa[key].assets.length > 0)).map(key => SYSTEM_LABELS[key]);
  const dates = fas.map(fa => fa.assessmentDate).filter((d): d is string => !!d).sort();
  const dateText = dates.length === 0 ? '—'
    : dates[0] === dates[dates.length - 1] ? formatCoverDate(dates[0])
      : `${formatCoverDate(dates[0])} – ${formatCoverDate(dates[dates.length - 1])}`;

  const colW = (cw - 10) / 2;
  const grid: [string, [string, string][]][] = [
    ['SCOPE', [
      ['Buildings assessed', String(fas.length)],
      ['Assets assessed', String(totalAssets2)],
      ['Systems covered', systems.length ? systems.join(', ') : '—'],
    ]],
    ['REPORT', [
      ['Assessment date', dateText],
      ['Report date', formatCoverDate(new Date().toISOString().slice(0, 10))],
      ['Prepared by', preparedBy],
    ]],
  ];
  const gridTop = c.y;
  let maxGridY = gridTop;
  grid.forEach(([label, rows], i) => {
    const x = m + i * (colW + 10);
    let ry = gridTop;
    c.ink(PDF.green); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    c.write(label, x, ry, { charSpace: 1 });
    ry += 2.5;
    doc.setDrawColor(...PDF.coverBorder); doc.line(x, ry, x + colW, ry);
    ry += 6.5;
    doc.setFontSize(9);
    rows.forEach(([k, v]) => {
      c.ink(PDF.coverInk); doc.setFont('helvetica', 'normal');
      c.write(k, x, ry);
      c.ink(PDF.slate); doc.setFont('helvetica', 'bold');
      // Most values are short (a date, a count) and fit on the row's baseline; a long one (e.g.
      // "Systems covered" with all four systems) wraps onto a second line instead of clipping.
      const valueLines = c.wrap(v, colW * 0.62).slice(0, 2);
      valueLines.forEach((ln, li) => c.write(ln, x + colW, ry + li * 4, { align: 'right' }));
      ry += valueLines.length > 1 ? 10.5 : 6.5;
    });
    maxGridY = Math.max(maxGridY, ry);
  });
  c.y = maxGridY;

  // ── Footer, pinned to the foot of the sheet: contact info left, report ID right. The
  // revision/page-count line is drawn later by finishReport(), once the final page count is
  // known — this page is always page 1, so it can't be counted yet. ──
  const footTop = ph - 26;
  doc.setDrawColor(...PDF.coverBorder); doc.line(m, footTop, pw - m, footTop);
  const footY = footTop + 5.5;
  c.ink(PDF.coverInk); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  c.write('3036C Piney Grove Church Road, Siler City, NC 27344', m, footY);
  c.write('919-930-5314  ·  info@sustainability-pros.com', m, footY + 4.3);
  c.write('www.sustainability-pros.com', m, footY + 8.6);

  c.ink(PDF.slate); doc.setFontSize(8); doc.setFont('courier', 'normal');
  const reportId = deriveReportId(data.project.name, new Date());
  c.write(reportId, pw - m, footY, { align: 'right' });
  registerCoverFooterPos(c.doc, 2, pw - m, footY + 4.3, 'right', PDF.coverInk);
}

/* finishReport() draws each cover page's "Rev 1 · N pages" line once it knows the true page
   count. A WeakMap only keys on the doc instance, and both cover pages share one doc, so each
   registers an entry (tagged with its own page number) onto a list rather than overwriting. */
const coverFooterReportId = new WeakMap<jsPDF, { page: number; x: number; y: number; align: 'left' | 'center' | 'right'; ink: RGB }[]>();
const registerCoverFooterPos = (
  doc: jsPDF, page: number, x: number, y: number, align: 'left' | 'center' | 'right', ink: RGB,
) => coverFooterReportId.set(doc, [...(coverFooterReportId.get(doc) ?? []), { page, x, y, align, ink }]);

export function buildDetailedReportPdf(
  data: ReportData,
  { logo, mark = null, markWhite = null, sitePhoto, preparedBy, photos = {} }: DetailedReportOptions,
): jsPDF {
  const c = startReport({
    logo, project: data.project, preparedBy, kicker: 'FACILITY ASSESSMENT - DETAILED',
    letterhead: false,
  });

  splitPanelTitlePage(c, data, preparedBy, markWhite, sitePhoto);
  c.doc.addPage();
  detailedCoverPage(c, data, preparedBy, mark);

  data.facilityAssessments.forEach(fa => {
    // The two cover pages own pages 1-2, so every assessment starts on its own fresh page after them.
    c.doc.addPage(); c.y = c.TOP;
    c.sectionHeading(fa.buildingName || 'Untitled Assessment');
    const overallMax = maxPossibleScore(fa);
    const overall = getBuildingRating(fa.overallScore ?? 0, overallMax);
    const total = assessedAssetCount(fa);
    c.body((overallMax > 0
      ? `Overall score: ${Math.round((fa.overallScore ?? 0) * 10) / 10} / ${overallMax}  —  ${overall}`
      : 'Overall: Not assessed')
      + `      ${total} asset${total === 1 ? '' : 's'} assessed`
      + (fa.assessmentDate ? `      Assessed: ${fa.assessmentDate}` : ''), 10);

    // Recorded but never reached a score/rating (isAssetAssessed needs both Visual Inspection and
    // Operational Test) — on a real walk that's almost always a unit that couldn't be reached, not
    // one the assessor forgot, so the report says so rather than leaving the gap unexplained.
    // Mirrors ReportDetailedView.tsx's on-screen twin.
    const notAssessed = totalAssetCount(fa) - total;
    if (notAssessed > 0) {
      c.doc.setFontSize(9); c.doc.setFont('helvetica', 'italic'); c.ink(PDF.muted);
      const line = `${notAssessed} asset${notAssessed === 1 ? '' : 's'} `
        + `${notAssessed === 1 ? 'was' : 'were'} not assessed due to lack of accessibility.`;
      c.checkPage(6);
      c.write(line, c.m, c.y);
      c.doc.setFont('helvetica', 'normal'); c.ink(PDF.slate);
      c.y += 5;
    }
    c.y += 4;

    SYSTEM_KEYS.forEach(key => {
      const sys = fa[key];
      if (sys.assets.length === 0) return;
      c.checkPage(24);
      c.subHeading(`${SYSTEM_LABELS[key]} — ${sys.assets.length} asset${sys.assets.length === 1 ? '' : 's'}`, 12);
      c.y += 2;
      sys.assets.forEach(a => assetCard(c, a, SYSTEM_LABELS[key], photos));
      c.y += 4;
    });
  });

  if (data.facilityAssessments.length === 0) {
    c.doc.addPage(); c.y = c.TOP;
    c.sectionHeading('Facility Asset Assessments');
    c.body('No assessment has been recorded for this project yet.', 10);
  }

  finishReport(c, data.project, { coverPages: 2 });
  return c.doc;
}

/** Detailed report download name. */
export const detailedReportFileName = (project: Project) =>
  reportFileName(project).replace(/-report\.pdf$/, '-detailed-report.pdf');
